import { API_URL } from "./constants";

// refine
import {
  ErrorComponent,
  ReadyPage,
  CssBaseline,
  createTheme,
  ThemeProvider,
  GlobalStyles,
  RefineSnackbarProvider,
  notificationProvider,
} from "@pankod/refine-mui";

// refine utils
import { Refine, Authenticated, CanAccess } from "@pankod/refine-core";
import routerProvider from "@pankod/refine-react-router-v6";
import { DataProvider } from "strapiDataProvider";
import { authProvider } from "authProvider";
import { accessControlProvider } from "accessControlProvider";

// i18n
import "locales/i18n";

// utils
import { AuthProvider } from "contexts/AuthContext";
import { QueryClient, QueryClientProvider } from "react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "utils/axios";

// custom pages and components
import CustomLayout from "components/CustomLayout";
import CustomCreateEdit from "components/CustomCreateEdit";
import CustomList from "components/CustomList";

import LoginPage from "components/user/LoginPage";
import RegisterPage from "components/user/RegisterPage";
import PasswordForgot from "components/user/PasswordForgot";
import PasswordReset from "components/user/PasswordReset";
import Dashboard from "pages/Dashboard";
import Settings from "pages/Settings";
import Unauthorized from "pages/Unauthorized";
import NotFound from "pages/NotFound";
import AccountDeleted from "pages/AccountDeleted";
import Privacy from "pages/Privacy";

// resources
import CustomerListConfig from "pages/clienti/ListConfig";
import CustomerFields from "pages/clienti/Fields";

import GestoriListConfig from "pages/gestori/ListConfig";
import GestoriFields from "pages/gestori/Fields";
import GestoriCreate from "pages/gestori/Create";

import StruttureListConfig from "pages/strutture/ListConfig";
import StruttureFields from "pages/strutture/Fields";
import StruttureQueryParams from "pages/strutture/queryParams";

import FacilityList from "pages/myFacilities/List";
import FacilityFields from "pages/myFacilities/Fields";
import MyFacilityQueryParams from "pages/myFacilities/queryParams";

import AccommodationList from "pages/accommodations/List";
import AccommodationFields from "pages/accommodations/Fields";
import AccommodationQueryParams from "pages/accommodations/queryParams";

import ExtraList from "pages/extras/List";
import ExtraFields from "pages/extras/Fields";
import ExtraQueryParams from "pages/extras/queryParams";

import ServiceTypeListConfig from "pages/serviceTypes/ListConfig";
import ServiceTypeFields from "pages/serviceTypes/Fields";

import FacilityServiceListConfig from "pages/facilityServices/ListConfig";
import FacilityServiceFields from "pages/facilityServices/Fields";

import FacilityTypeListConfig from "pages/facilityTypes/ListConfig";
import FacilityTypeFields from "pages/facilityTypes/Fields";

import FacilityCategoryListConfig from "pages/facilityCategories/ListConfig";
import FacilityCategoryFields from "pages/facilityCategories/Fields";

import AccommodationTypeListConfig from "pages/accommodationTypes/ListConfig";
import AccommodationTypeFields from "pages/accommodationTypes/Fields";

import AccommodationServiceListConfig from "pages/accommodationServices/ListConfig";
import AccommodationServiceFields from "pages/accommodationServices/Fields";

import CouponAdminList from "pages/couponsAdmin/List";
import CouponAdminFields from "pages/couponsAdmin/Fields";
import CouponAdminQueryParams from "pages/couponsAdmin/queryParams";

import BookingList from "pages/bookings/List";
import BookingFields from "pages/bookings/Fields";
import BookingQueryParams from "pages/bookings/queryParams";

import RatePlanListConfig from "pages/ratePlans/ListConfig";
import RatePlanEdit from "pages/ratePlans/Edit";
import RatePlanCreate from "pages/ratePlans/Create";

import RateTableauListConfig from "pages/rateTableau/ListConfig";
import RateTableau from "pages/rateTableau/Planner";

import ListinoListConfig from "pages/listini/ListConfig";
import ListinoFields from "pages/listini/Fields";

import ChildListConfig from "pages/children/ListConfig";
import ChildFields from "pages/children/Fields";

import EmailTemplatesList from "pages/email-templates/List";
import EmailTemplatesCreate from "pages/email-templates/Create";
import EmailTemplatesEdit from "pages/email-templates/Edit";

import TemplatesEditor from "pages/TemplatesEditor";

import StatisticheSistemazioni from "pages/statistics/StatisticheSistemazioni";
import StatisticheExtra from "pages/statistics/StatisticheExtra";
import StatisticheFunnel from "pages/statistics/StatisticheFunnel";

// Icons
import PeopleIcon from "@mui/icons-material/People";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HotelIcon from "@mui/icons-material/Hotel";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import InfoIcon from "@mui/icons-material/Info";
import SettingsIcon from "@mui/icons-material/Settings";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import EuroIcon from "@mui/icons-material/Euro";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import ChildCareIcon from "@mui/icons-material/ChildCare";
//import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BarChartIcon from "@mui/icons-material/BarChart";
import HomeIcon from "@mui/icons-material/Home";
import CheckIcon from "@mui/icons-material/Check";
import TouchAppIcon from "@mui/icons-material/TouchApp";

import { tr } from "date-fns/locale";

export const themeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: "#8d99ae",
    },
    secondary: {
      main: "#2b2d42",
    },
    info: {
      main: "#edf2f4",
      over: "#d0d4d6",
    },
    customBlue: {
      main: "#333C56",
      contrastText: "#fff",
    },
  },
};

const theme = createTheme(themeOptions);
const queryClient = new QueryClient();
window.queryClient = queryClient;

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
      <RefineSnackbarProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <Refine
              routerProvider={{
                ...routerProvider,
                routes: [
                  {
                    element: <RegisterPage />,
                    path: "/register",
                    layout: false,
                  },
                  {
                    element: <PasswordForgot />,
                    path: "/forgot-password",
                    layout: false,
                  },
                  {
                    element: <PasswordReset />,
                    path: "/reset-password",
                    layout: false,
                  },
                  {
                    element: <NotFound />,
                    path: "/not-found",
                    layout: false,
                  },
                  {
                    element: (
                      <Authenticated>
                        <Settings />
                      </Authenticated>
                    ),
                    path: "/settings",
                    layout: true,
                  },
                  {
                    element: (
                      <CanAccess action="admin" fallback={<Unauthorized />}>
                        <TemplatesEditor />
                      </CanAccess>
                    ),
                    path: "/email-templates",
                    layout: true,
                  },
                  {
                    element: <Privacy />,
                    path: "/privacy",
                    layout: false,
                  },
                  {
                    element: <AccountDeleted />,
                    path: "/account-deleted",
                    layout: false,
                  },
                ],
              }}
              //aggiungere access: "... ... ..."
              //dentro options per gestire cosa viene visualizzato nel menu in base al ruolo.
              //guarda Mie Strutture dove l'ho già messo.
              resources={[
                {
                  name: "Anagrafica",
                  options: {
                    label: "Anagrafica",
                    access: "authenticated owner",
                  },
                  icon: <InfoIcon />,
                },
                {
                  parentName: "Anagrafica", //per usare il menu a cascata
                  name: "users",
                  options: {
                    label: "Clienti",
                    access: "authenticated owner",
                  },
                  icon: <PeopleIcon />,
                  list: () => CustomList({ ...CustomerListConfig }),
                  /*create: () =>
                    CustomCreateEdit({
                      isCreate: true,
                      Fields: CustomerFields,
                      label: "cliente",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "Anagrafica",
                      routeName: "users",
                      isCreate: false,
                      Fields: CustomerFields,
                      label: "cliente",
                    }),*/
                  show: () =>
                    CustomCreateEdit({
                      parentName: "Anagrafica",
                      routeName: "users",
                      isCreate: false,
                      isShow: true,
                      Fields: CustomerFields,
                      label: "cliente",
                    }),
                },
                {
                  parentName: "Anagrafica",
                  name: "gestori",
                  options: {
                    label: "Gestori",
                    access: "authenticated",
                  },
                  icon: <VpnKeyIcon />,
                  list: () => CustomList({ ...GestoriListConfig }),
                  create: () =>
                    GestoriCreate({
                      parentName: "Anagrafica",
                      routeName: "gestori",
                      Fields: GestoriFields,
                      label: "gestore",
                    }),
                },
                {
                  parentName: "Anagrafica",
                  name: "strutture",
                  options: {
                    label: "Strutture",
                    access: "authenticated",
                  },
                  icon: <ApartmentIcon />,
                  list: () => CustomList({ ...StruttureListConfig }),
                  create: () =>
                    CustomCreateEdit({
                      parentName: "Anagrafica",
                      routeName: "strutture",
                      isCreate: true,
                      Fields: StruttureFields,
                      label: "struttura",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "Anagrafica",
                      routeName: "strutture",
                      isCreate: false,
                      Fields: StruttureFields,
                      label: "struttura",
                      queryParams: StruttureQueryParams,
                    }),
                },
                {
                  name: "IS",
                  options: {
                    label: "Le mie Strutture",
                    access: "owner",
                  },
                  icon: <ApartmentIcon />,
                },
                // Custom Resources ----------------------
                // {
                //   name: "",
                //   icon: <Icon />,
                //   options: { label: "Label" },
                //   list: List,
                //   create: Create,
                //   edit: Edit,
                // },
                // Standard Resources --------------------
                // {
                //   name: "recource-endpoint",
                //   options: { label: "Resource Name" },
                //   list: ResourceList,
                //   create: () =>
                //     CustomCreateEdit({
                //       isCreate: true,
                //       Fields: ResourceFields,
                //     }),
                //   edit: () =>
                //     CustomCreateEdit({
                //       isCreate: false,
                //       Fields: ResourceFields,
                //     }),
                // },
                {
                  parentName: "IS", //per usare il menu a cascata
                  name: "strutture",
                  options: {
                    label: "Dati strutture",
                    access: "owner",
                  },
                  icon: <InfoIcon />,
                  //list: () => CustomList({ ...FacilityListConfig }),
                  list: FacilityList,
                  /*create: () =>
                    CustomCreateEdit({
                      isCreate: true,
                      Fields: FacilityFields,
                    }),*/
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "IS",
                      routeName: "strutture",
                      isCreate: false,
                      Fields: FacilityFields,
                      label: "dati struttura",
                      queryParams: MyFacilityQueryParams,
                    }),
                },
                {
                  parentName: "IS",
                  name: "accommodations",
                  icon: <HotelIcon />,
                  options: { label: "Sistemazioni", access: "owner" },
                  list: AccommodationList,
                  create: () =>
                    CustomCreateEdit({
                      parentName: "IS",
                      routeName: "accommodations",
                      isCreate: true,
                      Fields: AccommodationFields,
                      label: "Sistemazione",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "IS",
                      routeName: "accommodations",
                      isCreate: false,
                      Fields: AccommodationFields,
                      label: "Sistemazione",
                      queryParams: AccommodationQueryParams,
                    }),
                },
                {
                  parentName: "IS", //per usare il menu a cascata
                  name: "extras",
                  icon: <PlaylistAddIcon />,
                  options: { label: "Servizi Extra", access: "owner" },
                  list: ExtraList,
                  create: () =>
                    CustomCreateEdit({
                      parentName: "IS",
                      routeName: "extras",
                      isCreate: true,
                      Fields: ExtraFields,
                      label: "Servizio Extra",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "IS",
                      routeName: "extras",
                      isCreate: false,
                      Fields: ExtraFields,
                      label: "Servizio Extra",
                      queryParams: ExtraQueryParams,
                    }),
                  /*show: () =>
                    CustomCreateEdit({
                      isCreate: false,
                      isShow: true,
                      Fields: ExtraFields,
                      label: "Servizio Extra",
                    }),*/
                },
                {
                  name: "coupons",
                  options: {
                    label: "Coupon",
                    access: "authenticated",
                  },
                  icon: <LocalActivityIcon />,
                  list: CouponAdminList,
                  create: () =>
                    CustomCreateEdit({
                      //parentName: "",
                      routeName: "coupons",
                      isCreate: true,
                      Fields: CouponAdminFields,
                      label: "coupon",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      //parentName: "",
                      routeName: "coupons",
                      isCreate: false,
                      Fields: CouponAdminFields,
                      label: "coupon",
                      defaultLangOnly: true,
                      queryParams: CouponAdminQueryParams,
                    }),
                },
                {
                  name: "prenotazioni",
                  options: {
                    label: "Prenotazioni",
                    access: "owner",
                  },
                  icon: <BookmarksIcon />,
                  list: BookingList,
                  show: () =>
                    CustomCreateEdit({
                      routeName: "prenotazioni",
                      isCreate: false,
                      isShow: true,
                      Fields: BookingFields,
                      label: "prenotazione",
                      queryParams: BookingQueryParams,
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      routeName: "prenotazioni",
                      isCreate: false,
                      Fields: BookingFields,
                      label: "prenotazione",
                    }),
                },
                {
                  name: "Tariffe",
                  options: {
                    label: "Tariffe e disponibilità",
                    access: "owner",
                  },
                  icon: <RequestQuoteIcon />,
                },
                {
                  parentName: "Tariffe",
                  name: "rate-plans",
                  icon: <EuroIcon />,
                  options: { label: "Piani tariffari", access: "owner" },
                  list: () => CustomList({ ...RatePlanListConfig }),
                  create: () =>
                    RatePlanCreate({
                      routeName: "rate-plans",
                      parentName: "Tariffe",
                    }),
                  edit: () =>
                    RatePlanEdit({
                      routeName: "rate-plans",
                      parentName: "Tariffe",
                    }),
                },
                {
                  parentName: "Tariffe",
                  name: "rate-tableau",
                  icon: <CalendarMonthIcon />,
                  options: { label: "Tableau", access: "owner" },
                  list: () => CustomList({ ...RateTableauListConfig }),
                  edit: RateTableau,
                },
                {
                  parentName: "Tariffe",
                  name: "listino-bookings",
                  icon: <GroupAddIcon />,
                  options: { label: "Listini", access: "owner" },
                  list: () => CustomList({ ...ListinoListConfig }),
                  create: () =>
                    CustomCreateEdit({
                      routeName: "listino-bookings",
                      parentName: "Tariffe",
                      isCreate: true,
                      Fields: ListinoFields,
                      label: "listino",
                      defaultLangOnly: true,
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      routeName: "listino-bookings",
                      parentName: "Tariffe",
                      isCreate: false,
                      Fields: ListinoFields,
                      label: "listino",
                    }),
                },
                // {
                //   parentName: "Tariffe",
                //   name: "age-ranges",
                //   icon: <ChildCareIcon />,
                //   options: { label: "Sconti minorenni", access: "owner" },
                //   list: () => CustomList({ ...ChildListConfig }),
                //   create: () =>
                //     CustomCreateEdit({
                //       routeName: "age-ranges",
                //       parentName: "Tariffe",
                //       isCreate: true,
                //       Fields: ChildFields,
                //       label: "sconto minorenni",
                //       defaultLangOnly: true,
                //     }),
                //   edit: () =>
                //     CustomCreateEdit({
                //       routeName: "age-ranges",
                //       parentName: "Tariffe",
                //       isCreate: false,
                //       Fields: ChildFields,
                //       label: "sconto minorenni",
                //     }),
                // },
                {
                  name: "Statistics",
                  options: {
                    label: "Statistiche",
                    access: "owner authenticated",
                  },
                  icon: <BarChartIcon />,
                },
                {
                  parentName: "Statistics",
                  name: "statisticsAccommodation",
                  icon: <HomeIcon />,
                  options: {
                    label: "Per Sistemazioni",
                    access: "owner",
                  },
                  list: StatisticheSistemazioni,
                },
                {
                  parentName: "Statistics",
                  name: "statisticsExtra",
                  icon: <CheckIcon />,
                  options: {
                    label: "Per Extra",
                    access: "owner",
                  },
                  list: StatisticheExtra,
                },
                {
                  parentName: "Statistics",
                  name: "statisticsFunnel",
                  icon: <TouchAppIcon />,
                  options: {
                    label: "Funnel",
                    access: "owner authenticated",
                  },
                  list: StatisticheFunnel,
                },
                {
                  name: "Configurations",
                  options: { label: "Configurazioni", access: "authenticated" },
                  icon: <SettingsIcon />,
                },
                {
                  parentName: "Configurations",
                  name: "facility-service-types",
                  icon: <SettingsSuggestIcon />,
                  options: {
                    label: "Tipologie Servizi",
                    access: "authenticated",
                  },
                  list: () => CustomList({ ...ServiceTypeListConfig }),
                  create: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "facility-service-types",
                      isCreate: true,
                      Fields: ServiceTypeFields,
                      label: "Tipologia Servizi",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "facility-service-types",
                      isCreate: false,
                      Fields: ServiceTypeFields,
                      label: "Tipologia Servizi",
                    }),
                },
                {
                  parentName: "Configurations",
                  name: "facility-services",
                  icon: <SettingsSuggestIcon />,
                  options: {
                    label: "Servizi strutture",
                    access: "authenticated",
                  },
                  list: () => CustomList({ ...FacilityServiceListConfig }),
                  create: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "facility-services",
                      isCreate: true,
                      Fields: FacilityServiceFields,
                      label: "Servizio strutture",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "facility-services",
                      isCreate: false,
                      Fields: FacilityServiceFields,
                      label: "Servizio strutture",
                    }),
                },
                {
                  parentName: "Configurations",
                  name: "facility-types",
                  icon: <SettingsSuggestIcon />,
                  options: {
                    label: "Tipologie strutture",
                    access: "authenticated",
                  },
                  list: () => CustomList({ ...FacilityTypeListConfig }),
                  create: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "facility-types",
                      isCreate: true,
                      Fields: FacilityTypeFields,
                      label: "Tipologia strutture",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "facility-types",
                      isCreate: false,
                      Fields: FacilityTypeFields,
                      label: "Tipologia strutture",
                    }),
                },
                {
                  parentName: "Configurations",
                  name: "facility-categories",
                  icon: <SettingsSuggestIcon />,
                  options: {
                    label: "Categorie strutture",
                    access: "authenticated",
                  },
                  list: () => CustomList({ ...FacilityCategoryListConfig }),
                  create: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "facility-categories",
                      isCreate: true,
                      Fields: FacilityCategoryFields,
                      label: "Categoria strutture",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "facility-categories",
                      isCreate: false,
                      Fields: FacilityCategoryFields,
                      label: "Categoria strutture",
                    }),
                },
                {
                  parentName: "Configurations",
                  name: "accommodation-types",
                  icon: <SettingsSuggestIcon />,
                  options: {
                    label: "Tipologie sistemazioni",
                    access: "authenticated",
                  },
                  list: () => CustomList({ ...AccommodationTypeListConfig }),
                  create: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "accommodation-types",
                      isCreate: true,
                      Fields: AccommodationTypeFields,
                      label: "Tipologia sistemazioni",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "accommodation-types",
                      isCreate: false,
                      Fields: AccommodationTypeFields,
                      label: "Tipologia sistemazioni",
                    }),
                },
                {
                  parentName: "Configurations",
                  name: "accommodation-services",
                  icon: <SettingsSuggestIcon />,
                  options: {
                    label: "Servizi sistemazioni",
                    access: "authenticated",
                  },
                  list: () => CustomList({ ...AccommodationServiceListConfig }),
                  create: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "accommodation-services",
                      isCreate: true,
                      Fields: AccommodationServiceFields,
                      label: "Servizio sistemazioni",
                    }),
                  edit: () =>
                    CustomCreateEdit({
                      parentName: "Configurations",
                      routeName: "accommodation-services",
                      isCreate: false,
                      Fields: AccommodationServiceFields,
                      label: "Servizio sistemazioni",
                    }),
                },
                // {
                //   parentName: "Configurations",
                //   name: "booking-email-templates",
                //   icon: <SettingsSuggestIcon />,
                //   options: {
                //     label: "Templates Email",
                //     access: "authenticated",
                //   },
                //   list: EmailTemplatesList,
                //   create: EmailTemplatesCreate,
                //   edit: EmailTemplatesEdit,
                // },
              ]}
              authProvider={authProvider}
              accessControlProvider={accessControlProvider}
              dataProvider={DataProvider(API_URL + "/api", axios)}
              notificationProvider={notificationProvider}
              Title={({ collapsed }) => (
                <div
                  style={{
                    height: 200,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {collapsed && (
                    <img src="/logo.png" alt="Logo" style={{ width: "50px" }} />
                  )}
                  {!collapsed && (
                    <img
                      src="/logo-big.png"
                      alt="Logo"
                      style={{ width: "90%" }}
                    />
                  )}
                </div>
              )}
              Layout={CustomLayout}
              DashboardPage={Dashboard}
              ReadyPage={ReadyPage}
              LoginPage={LoginPage}
              catchAll={<ErrorComponent />}
            />
          </AuthProvider>
        </QueryClientProvider>
      </RefineSnackbarProvider>
      <ToastContainer />
    </ThemeProvider>
  );
};

export default App;
